import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import { Card, CardContent, Snackbar, Alert } from "@mui/material";

import { AdminProjectContext } from "../../../contexts/AdminProjectContext";
import { AdminUserContext } from "../../../contexts/AdminUserContext";
import { ParticipantData } from "../../../interface/Participant.interface";
import { PaymentReminderRequest, PaymentReminderResponse } from "../../../interface/PaymentReminder.interface";
import useCurrentUser from "../../../hooks/useCurrentUser";
import { ADMIN_PROJECT_PARTICIPANTS_URL, ADMIN_PROJECT_PAYMENT_REMINDERS_URL, ADMIN_VIEWS } from "../../../utils/constant";
import useAxios from "../../../hooks/useAxios";
import logger from "../../../utils/logger";
import AdminAddProjectParticipantsModal from "../modal/AdminAddProjectParticipantsModal";
import AdminParticipantSettingModal from "../modal/AdminParticipantSettingModal";
import { PaymentReminderModal } from "../modal/PaymentReminderModal";
import {
    ParticipantsHeader,
    LoadingState,
    ErrorState,
    ParticipantCard
} from "./participants";
import "../../../styles/AdminActiveProjectUsersView.css";

interface AdminActiveProjectUsersViewProps {
    selectedMenu: (view: number) => void;
}

export default function AdminActiveProjectUsersView({ selectedMenu }: AdminActiveProjectUsersViewProps) {
    // Contexts and hooks
    const { selectedProject, refreshTrigger, setSelectedProject } = useContext(AdminProjectContext);
    const { updateSelectedProjectParticipant, selectedProjectParticipant } = useContext(AdminUserContext);
    const { currentUser } = useCurrentUser();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const { getWithAxios, postWithAxios } = useAxios();

    // State
    const [participants, setParticipants] = useState<ParticipantData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [openSettingModal, setOpenSettingModal] = useState(false);
    const [openNewParticipantModal, setOpenNewParticipantModal] = useState(false);

    // Payment reminder states
    const [openReminderModal, setOpenReminderModal] = useState(false);
    const [isSendingReminders, setIsSendingReminders] = useState(false);

    // Notification state
    const [notification, setNotification] = useState<{
        open: boolean;
        message: string;
        severity: "success" | "error";
    }>({ open: false, message: "", severity: "success" });

    // Computed values
    const lateParticipants = useMemo(() =>
            participants.filter(p => !p.upToDate),
        [participants]
    );

    // Load participants from selectedProject (already loaded in Provider)
    useEffect(() => {
        if (!selectedProject) {
            setParticipants([]);
            return;
        }

        const normalizedParticipants = (selectedProject.Users || []).map((participant: any) => ({
            uuid: participant.uuid,
            firstname: participant.firstname,
            lastname: participant.lastname,
            email: participant.email,
            upToDate: participant.UserProject?.upToDate ?? true,
            totalShare: participant.UserProject?.share || 0,
            shareConfigurations: participant.UserProject?.shareConfiguration || [],
            withdrawalRanks: participant.withdrawalRanks || [],
            withdrawals: participant.withdrawals || [],
            createdAt: participant.createdAt,
        }));

        setParticipants(normalizedParticipants);
    }, [selectedProject, refreshTrigger]);

    // Event handlers
    const handleCashInClick = (participant: ParticipantData) => {
        updateSelectedProjectParticipant(participant);
        selectedMenu(ADMIN_VIEWS.NEW_CASH_IN_VIEW);
    };

    const handleSettingClick = (participant: ParticipantData) => {
        updateSelectedProjectParticipant(participant);
        setOpenSettingModal(true);
    };

    const handleWithdrawOperationClick = (participant: ParticipantData) => {
        updateSelectedProjectParticipant(participant);
        selectedMenu(ADMIN_VIEWS.NEW_CASH_OUT_VIEW);
    };

    const handleAddParticipant = () => setOpenNewParticipantModal(true);

    const handleSendReminders = () => {
        if (lateParticipants.length > 0) setOpenReminderModal(true);
    };

    const handleConfirmSendReminders = async (
        selectedParticipants: ParticipantData[],
        customMessage?: string
    ) => {
        if (!selectedProject || selectedParticipants.length === 0) return;

        setIsSendingReminders(true);
        try {
            const request: PaymentReminderRequest = {
                participantIds: selectedParticipants.map(p => p.uuid),
                customMessage
            };

            const response: PaymentReminderResponse = await postWithAxios(
                ADMIN_PROJECT_PAYMENT_REMINDERS_URL(selectedProject.uuid!),
                request
            );

            setOpenReminderModal(false);
            setNotification({
                open: true,
                message: `Relances envoyées avec succès à ${response.data.sent} participant(s)`,
                severity: "success"
            });
        } catch (err) {
            logger.error('Error sending payment reminders:', err);
            const errorMessage = err instanceof Error ? err.message : "Erreur lors de l'envoi des relances";
            setNotification({
                open: true,
                message: `Erreur : ${errorMessage}`,
                severity: "error"
            });
        } finally {
            setIsSendingReminders(false);
        }
    };

    // Loading / Error state
    if (isLoading) return <LoadingState />;
    if (error) return <ErrorState error={error} />;

    return (
        <div className="users-container">
            <ParticipantsHeader
                onAddParticipant={handleAddParticipant}
                onSendReminders={handleSendReminders}
                lateParticipantsCount={lateParticipants.length}
            />

            <AdminParticipantSettingModal
                openModal={openSettingModal}
                setOpenModal={setOpenSettingModal}
                participant={selectedProjectParticipant}
            />

            <AdminAddProjectParticipantsModal
                openModal={openNewParticipantModal}
                setOpenModal={setOpenNewParticipantModal}
            />

            <PaymentReminderModal
                open={openReminderModal}
                onClose={() => setOpenReminderModal(false)}
                onConfirm={handleConfirmSendReminders}
                participants={lateParticipants}
                isLoading={isSendingReminders}
            />

            <div className="participants-grid" ref={containerRef}>
                {participants.map((participant, index) => (
                    <ParticipantCard
                        key={participant.uuid}
                        participant={participant}
                        index={index}
                        totalShares={selectedProject?.share || 0}
                        onCashIn={handleCashInClick}
                        onWithdraw={handleWithdrawOperationClick}
                        onSettings={handleSettingClick}
                    />
                ))}
            </div>

            {/* Snackbar notifications */}
            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={() => setNotification({ ...notification, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setNotification({ ...notification, open: false })}
                    severity={notification.severity}
                    sx={{ width: '100%' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </div>
    );
}
